import { useNotification } from "@dashboard/hooks/use-notification.hook";
import { validateCouponCode } from "@dashboard/modules/shared/components/onboarding-shared/services/coupons.services";
import { actions as onboardingActions } from "@dashboard/modules/shared/components/onboarding-shared/store/onboarding.slice";
import {
  clearPersistedSignupCoupon,
  normalizeSignupCoupon,
  persistSignupCoupon,
  readPersistedSignupCoupon,
} from "@dashboard/utils/signup-coupon.utils";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

type ValidateOptions = {
  coupon?: string;
  country?: string;
  token?: string;
};

export function useSignupCoupon() {
  const dispatch = useDispatch();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const validatedRef = useRef<string>("");

  const captureFromSearchParams = useCallback(
    (params: Record<string, string>) => {
      const coupon = normalizeSignupCoupon(params);
      if (!coupon) return "";

      dispatch(onboardingActions.setCouponCode(coupon));
      dispatch(
        onboardingActions.setUi({
          coupon,
        })
      );
      persistSignupCoupon(coupon);
      return coupon;
    },
    [dispatch]
  );

  const validateSignupCoupon = useCallback(
    async ({ coupon, country, token }: ValidateOptions) => {
      const resolvedCoupon = (coupon || readPersistedSignupCoupon()).trim();
      if (!resolvedCoupon || !token || !country) {
        return null;
      }

      if (validatedRef.current === `${resolvedCoupon}:${country}`) {
        return null;
      }

      validatedRef.current = `${resolvedCoupon}:${country}`;
      dispatch(onboardingActions.setCouponCode(resolvedCoupon));

      const result = await validateCouponCode(resolvedCoupon, country, token);

      switch (result.status) {
        case "free_onboarding":
          notify({
            type: "success",
            message: t(
              "onboarding.coupons.appliedFree",
              "Cupón válido: activarás tu membresía sin costo al completar el registro."
            ),
          });
          break;
        case "applied":
          notify({
            type: "success",
            message: t("onboarding.coupons.appliedDiscount", {
              defaultValue: "Cupón {{code}} aplicado. Verás el descuento al elegir tu plan.",
              code: result.coupon,
            }),
          });
          break;
        case "invalid":
          dispatch(onboardingActions.setCouponCode(""));
          clearPersistedSignupCoupon();
          notify({
            type: "error",
            message: t(
              "onboarding.coupons.invalidCode",
              "Código inválido."
            ),
          });
          break;
        case "error":
          notify({
            type: "error",
            message: t(
              "onboarding.coupons.validationError",
              "No pudimos validar el cupón. Intenta de nuevo más tarde."
            ),
          });
          break;
        default:
          break;
      }

      return result;
    },
    [dispatch, notify, t]
  );

  return {
    captureFromSearchParams,
    validateSignupCoupon,
    readPersistedSignupCoupon,
  };
}

export function useSignupCouponValidationEffect({
  token,
  country,
}: {
  token?: string;
  country?: string;
}) {
  const { validateSignupCoupon, readPersistedSignupCoupon } = useSignupCoupon();

  useEffect(() => {
    const coupon = readPersistedSignupCoupon();
    if (!coupon || !token || !country) return;

    void validateSignupCoupon({ coupon, country, token });
  }, [token, country, validateSignupCoupon, readPersistedSignupCoupon]);
}
